import React, { useEffect, useState } from 'react';
// import { useAuth } from '../../contexts/AuthContext'; // Unused
import { useUserRole } from '../../contexts/UserRoleContext';
import { supabaseClient } from '../../services/supabase';
import { sendTaskAssignmentNotification, getDeveloperInfo, getCurrentUserInfo } from '../../services/emailService';
// import { testLocalEmailNotification, createTestTaskData } from '../../utils/localEmailTest'; // Removed - file deleted
import './DevelopersLogPage.css';
import { Link } from 'react-router-dom';

interface Developer {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  assignedTo?: string;
  assignedDate?: string;
  completedBy?: string;
  completedDate?: string;
}

interface NewTask {
  title: string;
  description: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  assignedTo: string;
  assignedDevelopers: string[]; // New field for multiple developers
}

interface TaskCategory {
  title: string;
  tasks: Task[];
}

const SimpleDevelopersLogPage: React.FC = () => {
  // const { user } = useAuth(); // Unused for now
  const { hasRole } = useUserRole();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedDeveloper, setSelectedDeveloper] = useState<string>('all');
  const [showCreateTaskForm, setShowCreateTaskForm] = useState(false);
  const [newTask, setNewTask] = useState<NewTask>({
    title: '',
    description: '',
    category: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
    status: 'pending' as 'pending' | 'in_progress' | 'completed' | 'cancelled',
    assignedTo: '',
    assignedDevelopers: []
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);

  // Major accomplishments - now empty to avoid duplicates
  // const majorAccomplishments: Task[] = []; // Unused

  // Static developers list
  // State for developers fetched from Supabase
  const [developers, setDevelopers] = useState<Developer[]>([]);
  const [loadingDevelopers, setLoadingDevelopers] = useState(true);
  const [usingSupabaseData, setUsingSupabaseData] = useState(false);

  // State for tasks loaded from Supabase
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [usingDatabaseTasks, setUsingDatabaseTasks] = useState(false);

  // Fetch tasks from Supabase
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoadingTasks(true);
        const { data, error } = await supabaseClient
          .from('tasks')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching tasks:', error);
          // Fallback to empty array if Supabase fails
          setTasks([]);
          setUsingDatabaseTasks(false);
        } else {
          console.log('Fetched tasks from Supabase:', data);
          const formattedTasks = (data || []).map(task => ({
            id: task.id,
            title: task.title,
            description: task.description,
            category: task.category,
            priority: task.priority,
            status: task.status,
            assignedTo: task.assigned_to,
            completedBy: task.completed_by,
            completedDate: task.completed_at ? task.completed_at.split('T')[0] : undefined
          }));
          console.log('Formatted tasks:', formattedTasks);
          setTasks(formattedTasks);
          setUsingDatabaseTasks(true);
        }
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setTasks([]);
        setUsingDatabaseTasks(false);
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchTasks();
  }, []);

  // Fetch developers from Supabase
  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        setLoadingDevelopers(true);
        const { data, error } = await supabaseClient
          .from('user_profiles')
          .select('id, email, full_name, role')
          .in('role', ['super_admin', 'developer'])
          .order('full_name');

        if (error) {
          console.error('Error fetching developers:', error);
          // Fallback to static data if Supabase fails
          setDevelopers([
            { id: '1', name: 'Chris Jones', email: 'cmjones@berkeley.edu', role: 'super_admin' },
            { id: '2', name: 'CoolClimate Network', email: 'coolclimatenetwork@gmail.com', role: 'developer' },
            { id: '3', name: 'Travelling Chris', email: 'travellingchris@gmail.com', role: 'developer' },
          ]);
          setUsingSupabaseData(false);
        } else {
          console.log('Fetched developers from Supabase:', data);
          const formattedDevelopers = data.map(dev => ({
            id: dev.id,
            name: dev.full_name || dev.email,
            email: dev.email,
            role: dev.role
          }));
          console.log('Formatted developers:', formattedDevelopers);
          setDevelopers(formattedDevelopers);
          setUsingSupabaseData(true);
        }
      } catch (err) {
        console.error('Error fetching developers:', err);
        // Fallback to static data
        setDevelopers([
          { id: '1', name: 'Chris Jones', email: 'cmjones@berkeley.edu', role: 'super_admin' },
          { id: '2', name: 'CoolClimate Network', email: 'coolclimatenetwork@gmail.com', role: 'developer' },
          { id: '3', name: 'Travelling Chris', email: 'travellingchris@gmail.com', role: 'developer' },
        ]);
        setUsingSupabaseData(false);
      } finally {
        setLoadingDevelopers(false);
      }
    };

    fetchDevelopers();
  }, []);

  // Fetch existing task assignments from Supabase
  useEffect(() => {
    const fetchTaskAssignments = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('task_assignments')
          .select('task_id, user_id, assigned_at')
          .order('assigned_at', { ascending: false });

        if (error) {
          console.error('Error fetching task assignments:', error);
        } else {
          console.log('Fetched task assignments from Supabase:', data);
          
          // Update tasks with existing assignments (only for real tasks with UUID IDs)
          setTasks(prevTasks => 
            prevTasks.map(task => {
              // Only apply assignments to real tasks (UUID format)
              const isRealTask = task.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
              if (isRealTask) {
                const assignment = data.find(assignment => assignment.task_id === task.id);
                return assignment 
                  ? { 
                      ...task, 
                      assignedTo: assignment.user_id,
                      assignedDate: assignment.assigned_at ? assignment.assigned_at.split('T')[0] : task.assignedDate
                    }
                  : task;
              }
              return task; // Keep static tasks unchanged
            })
          );
        }
      } catch (err) {
        console.error('Error fetching task assignments:', err);
      }
    };

    fetchTaskAssignments();
  }, []);

  // Task creation functions
  const handleCreateTask = async () => {
    if (!newTask.title.trim() || !newTask.category.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    // Determine which developers to assign to
    const developersToAssign = newTask.assignedDevelopers.length > 0 
      ? newTask.assignedDevelopers 
      : (newTask.assignedTo ? [newTask.assignedTo] : []);

    if (developersToAssign.length === 0) {
      alert('Please assign the task to at least one developer');
      return;
    }

    try {
      const currentUser = await getCurrentUserInfo();
      const createdTasks: Task[] = [];
      const today = new Date().toISOString().split('T')[0];

      // Create a task copy for each assigned developer
      for (const developerId of developersToAssign) {
        const { data, error } = await supabaseClient
          .from('tasks')
          .insert({
            title: newTask.title,
            description: newTask.description || null,
            category: newTask.category,
            priority: newTask.priority,
            status: 'pending',
            assigned_to: developerId
          })
          .select()
          .single();

        if (error) {
          console.error(`Error creating task for developer ${developerId}:`, error);
          continue; // Continue with other developers even if one fails
        }

        // Add to local state
        const task: Task = {
          id: data.id,
          title: data.title,
          description: data.description,
          category: data.category,
          priority: data.priority,
          status: data.status,
          assignedTo: data.assigned_to,
          assignedDate: today
        };

        createdTasks.push(task);

        // Send email notification to this developer
        try {
          const assignedDeveloper = await getDeveloperInfo(developerId);

          if (assignedDeveloper && currentUser) {
            const notificationData = {
              taskId: data.id,
              title: data.title,
              description: data.description,
              category: data.category,
              priority: data.priority,
              assignedToEmail: assignedDeveloper.email,
              assignedToName: assignedDeveloper.full_name || assignedDeveloper.email.split('@')[0],
              assignedByEmail: currentUser.email,
              assignedByName: currentUser.full_name || currentUser.email.split('@')[0]
            };

            const emailResult = await sendTaskAssignmentNotification(notificationData);
            
            if (emailResult.success) {
              console.log(`✅ Task assignment email sent successfully to ${assignedDeveloper.email}`);
            } else {
              console.warn(`⚠️ Failed to send task assignment email to ${assignedDeveloper.email}:`, emailResult.error);
            }
          }
        } catch (emailError) {
          console.error(`Error sending task assignment email to developer ${developerId}:`, emailError);
          // Don't show error to user as task was created successfully
        }
      }

      // Update local state with all created tasks
      setTasks(prev => [...createdTasks, ...prev]);
      
      // Reset form
      setNewTask({
        title: '',
        description: '',
        category: '',
        priority: 'medium',
        status: 'pending',
        assignedTo: '',
        assignedDevelopers: []
      });
      setShowCreateTaskForm(false);
      setHasUnsavedChanges(true);

      // Show success message
      const taskCount = createdTasks.length;
      const developerCount = developersToAssign.length;
      if (taskCount === developerCount) {
        alert(`✅ Successfully created ${taskCount} task${taskCount > 1 ? 's' : ''} and assigned to ${developerCount} developer${developerCount > 1 ? 's' : ''}`);
      } else {
        alert(`⚠️ Created ${taskCount} out of ${developerCount} tasks. Some assignments may have failed.`);
      }

    } catch (error) {
      console.error('Error creating tasks:', error);
      alert('An error occurred while creating the tasks. Please try again.');
    }
  };

  const handleNewTaskChange = (field: string, value: string | string[]) => {
    setNewTask(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAssignedDevelopersChange = (developerId: string, checked: boolean) => {
    setNewTask(prev => ({
      ...prev,
      assignedDevelopers: checked 
        ? [...prev.assignedDevelopers, developerId]
        : prev.assignedDevelopers.filter(id => id !== developerId)
    }));
  };

  // Save functionality
  const handleSave = () => {
    // In a real app, this would save to a database
    // For now, we'll just show a success message
    alert('Changes saved successfully!');
    setHasUnsavedChanges(false);
  };

  // Move task from completed back to to-do
  const handleMoveToTodo = (taskId: string) => {
    // Update in Supabase
    supabaseClient
      .from('tasks')
      .update({ status: 'pending', completed_at: null })
      .eq('id', taskId)
      .then(({ error }) => {
        if (error) {
          console.error('Error updating task status:', error);
          alert('Failed to update task. Please try again.');
        } else {
          // Update local state
          setTasks(prev => prev.map(task => 
            task.id === taskId 
              ? { ...task, status: 'pending' as const, completedDate: undefined }
              : task
          ));
          setHasUnsavedChanges(true);
        }
      });
  };


  // Apply status filter then group tasks by category
  const filteredTasksByStatus = selectedStatus === 'all'
    ? tasks
    : tasks.filter(task => task.status === selectedStatus);
  const filteredTasksByPriority = selectedPriority === 'all'
    ? filteredTasksByStatus
    : filteredTasksByStatus.filter(task => task.priority === selectedPriority);

  const filteredTasksByDeveloper = selectedDeveloper === 'all'
    ? filteredTasksByPriority
    : filteredTasksByPriority.filter(task => (selectedDeveloper === 'unassigned' ? !task.assignedTo : task.assignedTo === selectedDeveloper));

  // Separate completed tasks from active tasks
  const activeTasks = filteredTasksByDeveloper.filter(task => task.status !== 'completed');
  const completedTasksFromDB = filteredTasksByDeveloper.filter(task => task.status === 'completed');

  const groupedTasks = activeTasks.reduce((acc, task) => {
    const category = task.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(task);
    return acc;
  }, {} as Record<string, Task[]>);

  const categories: TaskCategory[] = Object.entries(groupedTasks).map(([title, tasks]) => ({
    title,
    tasks: tasks as Task[]
  }));

  // Unique category list (unfiltered) for the category dropdown
  const allCategories = Array.from(new Set(tasks.map(t => t.category)));


  const handleAssignTask = async (taskId: string, developerId: string) => {
    try {
      // Get the task details before updating
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        console.error('Task not found');
        return;
      }

      // Update local state immediately for better UX
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                assignedTo: developerId === '' ? undefined : developerId,
                assignedDate: developerId === '' ? undefined : (task.assignedDate || new Date().toISOString().split('T')[0])
              }
            : task
        )
      );
      setHasUnsavedChanges(true);

      // Update the task in the database
      const { error: taskError } = await supabaseClient
        .from('tasks')
        .update({ assigned_to: developerId === '' ? null : developerId })
        .eq('id', taskId);

      if (taskError) {
        console.error('Error updating task assignment:', taskError);
        // Revert local state on error
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId 
              ? { ...task, assignedTo: undefined }
              : task
          )
        );
        alert('Failed to save task assignment. Please try again.');
        return;
      }

      // Send email notification if task is being assigned to someone
      if (developerId && developerId !== '') {
        try {
          const assignedDeveloper = await getDeveloperInfo(developerId);
          const currentUser = await getCurrentUserInfo();

          if (assignedDeveloper && currentUser) {
            const notificationData = {
              taskId: taskId,
              title: task.title,
              description: task.description,
              category: task.category,
              priority: task.priority,
              assignedToEmail: assignedDeveloper.email,
              assignedToName: assignedDeveloper.full_name || assignedDeveloper.email.split('@')[0],
              assignedByEmail: currentUser.email,
              assignedByName: currentUser.full_name || currentUser.email.split('@')[0]
            };

            const emailResult = await sendTaskAssignmentNotification(notificationData);
            
            if (emailResult.success) {
              console.log('✅ Task assignment email sent successfully');
            } else {
              console.warn('⚠️ Failed to send task assignment email:', emailResult.error);
            }
          }
        } catch (emailError) {
          console.error('Error sending task assignment email:', emailError);
          // Don't show error to user as task assignment was successful
        }
      }

      // Save to task_assignments table for tracking
      if (taskId && developerId) {
        console.log(`Saving task assignment: Task ${taskId} assigned to developer ${developerId}`);
        
        // Get current user ID for assigned_by field
        const { data: { user } } = await supabaseClient.auth.getUser();
        const assignedBy = user?.id;

        // Insert into task_assignments table
        const { error } = await supabaseClient
          .from('task_assignments')
          .insert({
            task_id: taskId,
            user_id: developerId,
            assigned_by: assignedBy
          });

        if (error) {
          console.error('Error saving task assignment:', error);
          // Don't revert the task update, just log the error
        } else {
          console.log('Task assignment saved successfully');
        }
      } else if (taskId && developerId === '') {
        // Handle unassignment - remove from task_assignments table
        console.log(`Removing task assignment for task ${taskId}`);
        
        const { error } = await supabaseClient
          .from('task_assignments')
          .delete()
          .eq('task_id', taskId);

        if (error) {
          console.error('Error removing task assignment:', error);
          // Don't show error to user, just log it
        } else {
          console.log('Task assignment removed successfully');
        }
      }
    } catch (error) {
      console.error('Error assigning task:', error);
      alert('An error occurred while assigning the task. Please try again.');
    }
  };

  const handlePriorityChange = (taskId: string, newPriority: 'high' | 'medium' | 'low') => {
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId 
          ? { ...task, priority: newPriority }
          : task
      )
    );
    setHasUnsavedChanges(true);
  };

  // Task status change function
  const handleTaskStatusChange = (taskId: string, newStatus: string) => {
    const validStatus = newStatus as 'pending' | 'in_progress' | 'completed' | 'cancelled';
    // let movedTask: Task | null = null; // Unused
    setTasks(prevTasks => {
      const updatedTasks = prevTasks.map(task => {
        if (task.id === taskId) {
          const updated: Task = {
            ...task,
            status: validStatus,
            completedBy: validStatus === 'completed' ? 'Chris Jones' : undefined,
            completedDate: validStatus === 'completed' ? new Date().toISOString().split('T')[0] : undefined
          };
          if (validStatus === 'completed') {
            // movedTask = updated; // Unused
            return null as unknown as Task; // mark for removal; will filter out below
          }
          return updated;
        }
        return task;
      });

      const filtered = updatedTasks.filter(Boolean) as Task[];
      return filtered;
    });

    // No need to handle completed tasks separately since they're now shown in the main list

    setHasUnsavedChanges(true);
  };

  // Task editing functions
  const handleEditTask = (taskId: string) => {
    setEditingTaskId(taskId);
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
  };

  const handleTaskFieldChange = (taskId: string, field: keyof Task, value: string) => {
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId 
          ? { ...task, [field]: value }
          : task
      )
    );
    setHasUnsavedChanges(true);
  };

  const handleSaveTaskEdit = (taskId: string) => {
    setEditingTaskId(null);
    setHasUnsavedChanges(true);
  };

  // Delete task function
  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete from Supabase
      const { error } = await supabaseClient
        .from('tasks')
        .delete()
        .eq('id', taskId);

      if (error) {
        console.error('Error deleting task:', error);
        alert('Failed to delete task. Please try again.');
        return;
      }

      // Update local state
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      setHasUnsavedChanges(true);
      
      console.log('Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('An error occurred while deleting the task. Please try again.');
    }
  };

  const getDeveloperName = (developerId?: string) => {
    if (!developerId) return 'Unassigned';
    const developer = developers.find(dev => dev.id === developerId);
    return developer ? developer.name : 'Unknown';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#ea580c';
      case 'low': return '#16a34a';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#16a34a';
      case 'in_progress': return '#ea580c';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const filteredCategories = selectedCategory === 'all' 
    ? categories 
    : categories.filter(cat => cat.title === selectedCategory);

  if (loadingTasks) {
    return (
      <div className="developers-log-page">
        <div className="developers-log-header">
          <h1>Developers' Log</h1>
          <p>Track and manage development tasks across the Climate Plans platform</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading tasks from database...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no tasks and not loading
  if (!loadingTasks && tasks.length === 0) {
    return (
      <div className="developers-log-page">
        <div className="developers-log-header">
          <h1>Developers' Log</h1>
          <p>Track and manage development tasks across the Climate Plans platform</p>
        </div>
        <div className="empty-state">
          <h2>No Tasks Found</h2>
          <p>The database doesn't have any tasks yet. You can either:</p>
          <ul>
            <li>Run the migration script to populate with initial tasks</li>
            <li>Create new tasks using the form below</li>
          </ul>
          <div className="setup-notice">
            <strong>Quick Setup:</strong> Use the inline SQL in <code>SUPABASE_SETUP.md</code> to initialize tables and seed tasks.
          </div>
        </div>
        
        {/* Show create task form even when empty */}
        <div className="developers-log-controls">
          <div className="control-buttons">
            <button 
              className="create-task-button"
              onClick={() => setShowCreateTaskForm(!showCreateTaskForm)}
            >
              {showCreateTaskForm ? 'Cancel' : '+ Create New Task'}
            </button>
            {/* Local development email test button */}
            <button 
              className="test-email-button"
              onClick={() => {
                console.log('🔍 Current location:', {
                  hostname: window.location.hostname,
                  port: window.location.port,
                  href: window.location.href
                });
                console.log('Test email button clicked (functionality removed)');
              }}
              style={{
                marginLeft: '10px',
                padding: '8px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              📧 Test Email (Debug)
            </button>
          </div>
        </div>

        {/* Create Task Form */}
        {showCreateTaskForm && (
          <div className="create-task-form">
            <h3>Create New Task</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="task-title">Task Title *</label>
                <input
                  id="task-title"
                  type="text"
                  value={newTask.title}
                  onChange={(e) => handleNewTaskChange('title', e.target.value)}
                  placeholder="Enter task title"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="task-category">Category *</label>
                <select
                  id="task-category"
                  value={newTask.category}
                  onChange={(e) => handleNewTaskChange('category', e.target.value)}
                  required
                >
                  <option value="">Select a category</option>
                  <option value="Bug Fixes">Bug Fixes</option>
                  <option value="Security">Security</option>
                  <option value="Data Fixes">Data Fixes</option>
                  <option value="Performance">Performance</option>
                  <option value="UI/UX">UI/UX</option>
                  <option value="Authentication">Authentication</option>
                  <option value="Project Management">Project Management</option>
                  <option value="Energy Features">Energy Features</option>
                  <option value="Transportation Features">Transportation Features</option>
                  <option value="Map Improvements">Map Improvements</option>
                  <option value="New Features">New Features</option>
                  <option value="People Features">People Features</option>
                  <option value="Core Features">Core Features</option>
                  <option value="Backend">Backend</option>
                  <option value="Foundation">Foundation</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="task-priority">Priority</label>
                <select
                  id="task-priority"
                  value={newTask.priority}
                  onChange={(e) => handleNewTaskChange('priority', e.target.value)}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="task-status">Status</label>
                <select
                  id="task-status"
                  value={newTask.status}
                  onChange={(e) => handleNewTaskChange('status', e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In-Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Assign To (Multiple Selection)</label>
                <div className="developer-checkboxes" style={{ 
                  maxHeight: '120px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px', 
                  padding: '12px',
                  backgroundColor: '#f9f9f9',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '8px'
                }}>
                  {loadingDevelopers ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#666' }}>
                      Loading developers...
                    </div>
                  ) : (
                    developers.map(dev => (
                      <label key={dev.id} style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '3px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={newTask.assignedDevelopers.includes(dev.id)}
                          onChange={(e) => handleAssignedDevelopersChange(dev.id, e.target.checked)}
                          style={{ marginRight: '8px', marginTop: '0' }}
                        />
                        <span style={{ fontSize: '14px' }}>
                          {dev.name} <span style={{ color: '#666', fontSize: '12px' }}>({dev.role.replace('_', ' ').toUpperCase()})</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
                {newTask.assignedDevelopers.length > 0 && (
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '12px', 
                    color: '#666',
                    fontWeight: '500'
                  }}>
                    ✓ Selected: {newTask.assignedDevelopers.length} developer{newTask.assignedDevelopers.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <div className="form-group full-width">
                <label htmlFor="task-description">Description</label>
                <textarea
                  id="task-description"
                  value={newTask.description}
                  onChange={(e) => handleNewTaskChange('description', e.target.value)}
                  placeholder="Enter task description (optional)"
                  rows={3}
                />
              </div>
            </div>
            <div className="form-actions">
              <button className="create-button" onClick={handleCreateTask}>
                Create Task
              </button>
              <button className="cancel-button" onClick={() => setShowCreateTaskForm(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="developers-log-page">
        <div className="developers-log-header">
        <h1>Developers' Log</h1>
        <p>Track and manage development tasks across the Climate Plans platform</p>
        <div className="setup-notice">
          <strong>Note:</strong> Tasks are loaded from the database. If empty, use inline snippets in <code>SUPABASE_SETUP.md</code> to seed.
        </div>
        {!loadingDevelopers && !loadingTasks && (
          <div className={`data-source-indicator ${usingSupabaseData && usingDatabaseTasks ? 'supabase' : 'fallback'}`}>
            {usingSupabaseData && usingDatabaseTasks ? (
              <span>✅ Using Supabase data ({developers.length} developers, {tasks.length} tasks loaded)</span>
            ) : (
              <span>⚠️ Using fallback data (Supabase connection failed)</span>
            )}
          </div>
        )}
      </div>

      {/* Developer Pages Menu */}
      {!loadingDevelopers && developers.length > 0 && (
        <div className="developer-pages-menu">
          <h3>Developer Pages</h3>
          <p>Visit individual developer pages to see their contributions and status.</p>
          <div className="developer-links">
            {developers.map(dev => {
              // Create slug: if full_name missing (name looks like email), use email prefix; otherwise use first name
              const emailPrefix = dev.email.split('@')[0].toLowerCase();
              const isEmailAsName = dev.name.includes('@');
              const firstName = isEmailAsName ? '' : (dev.name.split(' ')[0] || '').toLowerCase();
              const slug = firstName || emailPrefix;

              return (
                <Link 
                  key={dev.id}
                  to={`/developer/${slug}`}
                  className="developer-link"
                  title={`Visit ${dev.name}'s page`}
                >
                  <span className="developer-name">{dev.name}</span>
                  <span className="developer-role">{dev.role.replace('_', ' ')}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="developers-log-controls">
        <div className="category-filter">
          <label htmlFor="category-filter">Filter by:</label>
          <select
            id="category-filter"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {allCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div className="status-filter">
          <label htmlFor="status-filter">By Status:</label>
          <select
            id="status-filter"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In-Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="priority-filter">
          <label htmlFor="priority-filter">By Priority:</label>
          <select
            id="priority-filter"
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
          >
            <option value="all">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div className="developer-filter">
          <label htmlFor="developer-filter">By Developer:</label>
          <select
            id="developer-filter"
            value={selectedDeveloper}
            onChange={(e) => setSelectedDeveloper(e.target.value)}
            disabled={loadingDevelopers}
          >
            <option value="all">All Developers</option>
            <option value="unassigned">Unassigned</option>
            {developers.map(dev => (
              <option key={dev.id} value={dev.id}>
                {dev.name}
              </option>
            ))}
          </select>
        </div>
        <div className="control-buttons">
          {(hasRole('super_admin') || hasRole('local_govt_admin') || hasRole('developer')) && (
            <button 
              className="create-task-button"
              onClick={() => setShowCreateTaskForm(!showCreateTaskForm)}
            >
              {showCreateTaskForm ? 'Cancel' : '+ Create New Task'}
            </button>
          )}
          {/* Local development email test button */}
          <button 
            className="test-email-button"
            onClick={() => {
              console.log('🔍 Current location:', {
                hostname: window.location.hostname,
                port: window.location.port,
                href: window.location.href
              });
              console.log('Test email button clicked (functionality removed)');
            }}
            style={{
              marginLeft: '10px',
              padding: '8px 16px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            📧 Test Email (Debug)
          </button>
          <button 
            className="save-button"
            onClick={handleSave}
            disabled={!hasUnsavedChanges}
            title={hasUnsavedChanges ? 'Save changes' : 'No changes to save'}
          >
            💾 Save Changes
          </button>
        </div>
      </div>

      {/* Create Task Form */}
      {showCreateTaskForm && (
        <div className="create-task-form">
          <h3>Create New Task</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="task-title">Task Title *</label>
              <input
                id="task-title"
                type="text"
                value={newTask.title}
                onChange={(e) => handleNewTaskChange('title', e.target.value)}
                placeholder="Enter task title"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="task-category">Category *</label>
              <select
                id="task-category"
                value={newTask.category}
                onChange={(e) => handleNewTaskChange('category', e.target.value)}
                required
              >
                <option value="">Select a category</option>
                <option value="Bug Fixes">Bug Fixes</option>
                <option value="Security">Security</option>
                <option value="Data Fixes">Data Fixes</option>
                <option value="Performance">Performance</option>
                <option value="UI/UX">UI/UX</option>
                <option value="Authentication">Authentication</option>
                <option value="Project Management">Project Management</option>
                <option value="Energy Features">Energy Features</option>
                <option value="Transportation Features">Transportation Features</option>
                <option value="Map Improvements">Map Improvements</option>
                <option value="New Features">New Features</option>
                <option value="People Features">People Features</option>
                <option value="Core Features">Core Features</option>
                <option value="Backend">Backend</option>
                <option value="Foundation">Foundation</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="task-priority">Priority</label>
              <select
                id="task-priority"
                value={newTask.priority}
                onChange={(e) => handleNewTaskChange('priority', e.target.value)}
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="task-status">Status</label>
              <select
                id="task-status"
                value={newTask.status}
                onChange={(e) => handleNewTaskChange('status', e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In-Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div className="form-group full-width">
              <label>Assign To (Multiple Selection)</label>
              <div className="developer-checkboxes" style={{ 
                maxHeight: '120px', 
                overflowY: 'auto', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '12px',
                backgroundColor: '#f9f9f9',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '8px'
              }}>
                {loadingDevelopers ? (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#666' }}>
                    Loading developers...
                  </div>
                ) : (
                  developers.map(dev => (
                    <label key={dev.id} style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '3px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={newTask.assignedDevelopers.includes(dev.id)}
                        onChange={(e) => handleAssignedDevelopersChange(dev.id, e.target.checked)}
                        style={{ marginRight: '8px', marginTop: '0' }}
                      />
                      <span style={{ fontSize: '14px' }}>
                        {dev.name} <span style={{ color: '#666', fontSize: '12px' }}>({dev.role.replace('_', ' ').toUpperCase()})</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
              {newTask.assignedDevelopers.length > 0 && (
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '12px', 
                  color: '#666',
                  fontWeight: '500'
                }}>
                  ✓ Selected: {newTask.assignedDevelopers.length} developer{newTask.assignedDevelopers.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
            <div className="form-group full-width">
              <label htmlFor="task-description">Description</label>
              <textarea
                id="task-description"
                value={newTask.description}
                onChange={(e) => handleNewTaskChange('description', e.target.value)}
                placeholder="Enter task description (optional)"
                rows={3}
              />
            </div>
          </div>
          <div className="form-actions">
            <button className="create-button" onClick={handleCreateTask}>
              Create Task
            </button>
            <button className="cancel-button" onClick={() => setShowCreateTaskForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {filteredCategories.map((category) => (
        <div key={category.title} className="task-category">
          <h2>{category.title}</h2>
          <div className="task-list">
            {category.tasks.map((task) => (
              <div key={task.id} className={`task-item status-${task.status}`}>
                <div className="priority-selector">
                  <label htmlFor={`priority-${task.id}`} className="priority-label">
                    Priority
                  </label>
                  {(hasRole('super_admin') || hasRole('local_govt_admin') || hasRole('developer')) ? (
                    <select
                      id={`priority-${task.id}`}
                      value={task.priority}
                      onChange={(e) => handlePriorityChange(task.id, e.target.value as 'high' | 'medium' | 'low')}
                      className="priority-select"
                      style={{ backgroundColor: getPriorityColor(task.priority) }}
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  ) : (
                    <span className="priority-display" style={{ backgroundColor: getPriorityColor(task.priority) }}>
                      {task.priority.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="status-selector">
                  <label htmlFor={`status-${task.id}`} className="status-label">
                    Status
                  </label>
                  {(hasRole('super_admin') || hasRole('local_govt_admin') || hasRole('developer')) ? (
                    <select
                      id={`status-${task.id}`}
                      value={task.status}
                      onChange={(e) => handleTaskStatusChange(task.id, e.target.value)}
                      className="status-select"
                      style={{ backgroundColor: getStatusColor(task.status) }}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In-Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  ) : (
                    <span className="status-display" style={{ backgroundColor: getStatusColor(task.status) }}>
                      {task.status.replace('_', '-').toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="task-content">
                  {editingTaskId === task.id ? (
                    <div className="editable-fields">
                      <input
                        type="text"
                        value={task.title}
                        onChange={(e) => handleTaskFieldChange(task.id, 'title', e.target.value)}
                        className="editable-title"
                        placeholder="Enter task title"
                      />
                      <textarea
                        value={task.description || ''}
                        onChange={(e) => handleTaskFieldChange(task.id, 'description', e.target.value)}
                        className="editable-description"
                        placeholder="Enter task description (optional)"
                        rows={2}
                      />
                    </div>
                  ) : (
                    <div className={`task-display ${(hasRole('super_admin') || hasRole('local_govt_admin') || hasRole('developer')) ? 'editable' : ''}`} 
                         onClick={(hasRole('super_admin') || hasRole('local_govt_admin') || hasRole('developer')) ? () => handleEditTask(task.id) : undefined}>
                      <div className="task-title">
                        {task.title}
                      </div>
                      {task.description && (
                        <div className="task-description">
                          {task.description}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className={`task-assignment ${task.assignedTo ? 'assigned' : ''}`}>
                  {(hasRole('super_admin') || hasRole('local_govt_admin') || hasRole('developer')) ? (
                    <>
                      <select
                        id={`assign-${task.id}`}
                        value={task.assignedTo || ''}
                        onChange={(e) => handleAssignTask(task.id, e.target.value)}
                        className="assign-select"
                        disabled={loadingDevelopers}
                      >
                        <option value="">
                          {loadingDevelopers ? 'Loading...' : 'Unassigned'}
                        </option>
                        {developers.map(dev => (
                          <option key={dev.id} value={dev.id}>
                            {dev.name} ({dev.role.replace('_', ' ').toUpperCase()})
                          </option>
                        ))}
                      </select>
                      {task.assignedTo && task.assignedDate && (
                        <span className="assigned-date">Assigned: {task.assignedDate}</span>
                      )}
                    </>
                  ) : (
                    <span className="assignment-display">
                      {task.assignedTo ? getDeveloperName(task.assignedTo) : 'Unassigned'}{task.assignedDate ? ` • Assigned: ${task.assignedDate}` : ''}
                    </span>
                  )}
                </div>
                {editingTaskId === task.id && (
                  <div className="edit-actions">
                    <button 
                      className="save-edit-button" 
                      onClick={() => handleSaveTaskEdit(task.id)}
                      title="Save changes"
                    >
                      💾 Save
                    </button>
                    <button 
                      className="cancel-edit-button" 
                      onClick={handleCancelEdit}
                      title="Cancel editing"
                    >
                      ❌ Cancel
                    </button>
                    <button 
                      className="delete-task-button" 
                      onClick={() => handleDeleteTask(task.id)}
                      title="Delete task"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Completed Tasks Section */}
      {completedTasksFromDB.length > 0 && (
        <div className="completed-tasks-section">
          <h2>✅ Completed Tasks</h2>
          <div className="completed-tasks-list">
            {/* Recently completed tasks */}
            {completedTasksFromDB.map((task) => (
              <div key={task.id} className="completed-task-item">
                <span 
                  className="priority-badge"
                  style={{ backgroundColor: getPriorityColor(task.priority) }}
                >
                  {task.priority.toUpperCase()}
                </span>
                <span 
                  className="status-badge completed"
                  style={{ backgroundColor: getStatusColor('completed') }}
                >
                  COMPLETED
                </span>
                <div className="completed-task-title">
                  {task.title}
                </div>
                {task.description && (
                  <div className="task-description">
                    {task.description}
                  </div>
                )}
                <div className="completed-task-meta">
                  <span className="completed-date">
                    Completed: {task.completedDate || 'Recently'}
                  </span>
                  <span className="assigned-to">
                    Developer: {getDeveloperName(task.assignedTo) || 'Unassigned'}{task.assignedDate ? ` • Assigned: ${task.assignedDate}` : ''}
                  </span>
                </div>
                <div className="completed-task-actions">
                  <button 
                    className="move-to-todo-button"
                    onClick={() => handleMoveToTodo(task.id)}
                    title="Move back to To-Do list"
                  >
                    ↩️ Move to To-Do
                  </button>
                  <button 
                    className="delete-task-button" 
                    onClick={() => handleDeleteTask(task.id)}
                    title="Delete task"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleDevelopersLogPage;
